import { Bay, PalletConfigObj } from 'blackbox';
import { Application, StackQueue, StackQueueItem } from 'libapi';
import { ReactElement, useState, useEffect, useContext } from 'react';
import CheckBox from '../common/Checkbox';
import Slider from '../common/Slider';
import {
	useCoreAppConfig,
	getBarcodeStartModeEnabled,
	getConfigValueFromKey,
} from '../General';
import { lBlueFilter } from '../operation/OperationPage';
import Button from '../PendantButton';
import DirectionalButton from '../shared/DirectionalButton';
import { COLOR } from '../shared/shared';
import StackScene from '../three/StackScene';
import { Position } from './OperationPage';
import close from '../../images/close.png';
import { ApplicationContext } from '../../App';

function getTooltipText(
	positions: { type: string; layer: number; position: number; id: string }[],
	index: number
) {
	const position = positions[index];
	if (position.type === 'Slipsheet') {
		const filteredPositions = positions.filter(p => p.type === position.type);
		const i = filteredPositions.indexOf(position);
		return `${position.type} ${i + 1} of ${filteredPositions.length}`;
	} else {
		return `Layer: ${position.layer + 1} \n Box: ${position.position + 1}`;
	}
}

const getStackAndSkuTitle = (
	item: StackQueueItem,
	barcodeStartMode: boolean,
	palletConfig: PalletConfigObj
) => {
	let title = '';
	const pallet = palletConfig.config.pallets[item.PalletID];
	if (pallet) {
		const stack = pallet.Stack[item.StackID];
		title += stack.name;
	}
	if (barcodeStartMode) {
		const box = palletConfig.config.boxes[item.BoxIndex];
		if (box) {
			title += ` ${box.skuId}`;
		}
	}
	return title;
};

// -----------------------------------------
// Modal Wrapper with Steps
// -----------------------------------------

interface StartDialogProps {
	title: string;
	stepTitles: string[];
	activeStep: number;
	activeStepReady: boolean;
	setActiveStep: (pStep: number) => void;
	handleExit: (pAbort: boolean) => void;
	handleStart: () => void;
	waitingForConfirm: boolean;
	children: ReactElement;
}

function StartDialog({
	title,
	stepTitles,
	activeStep,
	activeStepReady,
	setActiveStep,
	handleExit,
	handleStart,
	waitingForConfirm,
	children,
}: StartDialogProps) {
	const [completedSteps, setCompletedSteps] = useState<boolean[]>([]);
	const [allCompleted, setAllCompleted] = useState<boolean>(false);

	useEffect(() => {
		// Init state
		if (stepTitles.length === 0) return;
		const existing = [...completedSteps];
		const completed = [];
		for (let i = 0; i < stepTitles.length; i++) {
			if (i < existing.length) {
				completed.push(existing[i]);
			} else {
				completed.push(false);
			}
		}
		setCompletedSteps(completed);
	}, [stepTitles]);

	useEffect(() => {
		setAllCompleted(completedSteps.filter(s => !s).length === 0);
	}, [completedSteps]);

	function handleNext() {
		const completed = [...completedSteps];
		completed[activeStep] = true;
		setCompletedSteps(completed);
		if (activeStep < stepTitles.length - 1) {
			setActiveStep(activeStep + 1);
		}
	}

	function handleStepClick(index: number) {
		if (!activeStepReady && index >= activeStep) return;
		// If it's the current step
		if (index === activeStep) {
			// complete it
			const completed = [...completedSteps];
			completed[index] = true;
			setCompletedSteps(completed);
		} else {
			setActiveStep(index);
		}
	}

	return (
		<div className="full-size flex-col">
			<div className="background-silver full-width rounded-top padding-bottom flex-row flex-between align-center double-margin-bottom">
				<h1 className="font-6 bold-font color-blue double-padding-left double-padding-top">
					{title}
				</h1>
				{waitingForConfirm && (
					<Button
						pAction={() => handleExit(true)}
						pNormalColorOverride={COLOR.RED_CLEAR}
						pClickColorOverride={COLOR.RED}
						pClass="double-margin-right margin-top"
					>
						<span className="font-6 bold-font double-padding">STOP</span>
					</Button>
				)}
				{!waitingForConfirm && (
					<Button
						pAction={() => handleExit(false)}
						pNormalColorOverride={COLOR.TRANSPARENT}
						className="double-margin-top height-60 double-margin-right"
					>
						<img
							className="full-height auto-width"
							style={{ filter: lBlueFilter }}
							src={close}
							alt="close"
						/>
					</Button>
				)}
			</div>
			<section className="flex-row flex-between width-100 height-78 double-margin-bottom">
				{/* Left Pane */}
				<div className="double-margin-left flex-col full-height align-center width-23 container-border padding">
					<div className="flex-col full-height full-width">
						{stepTitles.map(function (step, stepIndex) {
							return (
								<CheckBox
									key={stepIndex}
									pCompleted={completedSteps[stepIndex]}
									pActive={stepIndex === activeStep}
									pText={step}
									pIndex={stepIndex}
									pHandleClick={(pIndex: number) => {
										handleStepClick(pIndex);
									}}
								/>
							);
						})}
					</div>
				</div>
				{/* Right pane */}
				<div className="flex-col flex-between align-center width-65 full-height container-border double-margin-right padding">
					{children}
					{/* Buttons */}
					<div className="flex-row full-width flex-between">
						<Button
							pDisabled={stepTitles.length === 1}
							pAction={() => {
								if (activeStep > 0) {
									setActiveStep(activeStep - 1);
								}
							}}
							pNormalColorOverride={COLOR.TRANSPARENT}
							className={`width-20 ${
								activeStep === 0 ? 'inner-border' : 'blue-border'
							}`}
						>
							<span
								className={`larger-font color-blue ${
									activeStep === 0 ? 'color-dark-grey' : 'color-blue'
								}`}
							>
								Back
							</span>
						</Button>
						<Button
							pAction={() => {
								if (allCompleted) {
									handleExit(false);
									handleStart();
								} else handleNext();
							}}
							pNormalColorOverride={
								activeStepReady
									? allCompleted
										? COLOR.GREEN
										: COLOR.MEDIUM_BLUE
									: COLOR.DARK_GREY
							}
							pDisabled={!activeStepReady}
							className="width-20"
						>
							<span className="larger-font">{allCompleted ? 'Start' : 'Confirm'}</span>
						</Button>
					</div>
				</div>
			</section>
		</div>
	);
}

// -----------------------------------------
// Choose number of pallets if multi-pallet
// -----------------------------------------

interface MultiPalletDialogProps {
	bayQueueItems: StackQueueItem[];
	bay: number;
	position: number;
	bayPalletMap: { [key: number]: number };
	setBayPalletMap: (map: { [key: number]: number }) => void;
	palletConfig: PalletConfigObj;
}

function MultiPalletDialog({
	bay,
	bayQueueItems,
	position,
	bayPalletMap,
	setBayPalletMap,
	palletConfig,
}: MultiPalletDialogProps) {
	const lStackForBay = bayQueueItems[bay];
	if (!lStackForBay) return <></>;
	return (
		<div
			key={bay}
			className="full-width flex-col flex-between align-center full-height"
		>
			<span className="text-align-center font-5 color-blue double-margin-bottom">
				Verify the number of starting pallets
			</span>
			<div className="full-width flex-row flex-around align-center full-height">
				<div className="flex-col flex-center full-height">
					<>
						<span className="font-5 bold-font color-blue">Number of Pallets</span>
						<div className="half-padding" />
						<div className="double-padding"></div>
						<Button
							pAction={() => {
								const lTemp = { ...bayPalletMap };
								lTemp[lStackForBay.BayID] = 0;
								setBayPalletMap(lTemp);
							}}
							className="full-width"
							pNormalColorOverride={
								bayPalletMap[lStackForBay.BayID] === 0 ? COLOR.GREEN : COLOR.MEDIUM_BLUE
							}
						>
							<span>1 Pallet</span>
						</Button>
						<div className="double-padding"></div>
						<Button
							pAction={() => {
								const lTemp = { ...bayPalletMap };
								lTemp[lStackForBay.BayID] = 1;
								setBayPalletMap(lTemp);
							}}
							className="full-width"
							pNormalColorOverride={
								bayPalletMap[lStackForBay.BayID] === 1 ? COLOR.GREEN : COLOR.MEDIUM_BLUE
							}
						>
							<span>2 Pallets</span>
						</Button>
						<div className="double-padding"></div>
						<Button
							pAction={() => {
								const lTemp = { ...bayPalletMap };
								lTemp[lStackForBay.BayID] = 2;
								setBayPalletMap(lTemp);
							}}
							className="full-width"
							pNormalColorOverride={
								bayPalletMap[lStackForBay.BayID] === 2 ? COLOR.GREEN : COLOR.MEDIUM_BLUE
							}
						>
							<span>3 Pallets</span>
						</Button>
					</>
				</div>
				<div className="flex-col flex-center  semi-transparent">
					<StackScene
						palletConfig={palletConfig}
						palletIndex={lStackForBay.PalletID}
						stackIndex={lStackForBay.StackID}
						bayIndex={bay}
						position={position}
					/>
				</div>
			</div>
		</div>
	);
}

// -----------------------------------------
// Choose starting position
// -----------------------------------------

interface StartingPositionDialogProps {
	bay: StackQueueItem;
	palletConfig: PalletConfigObj;
	positions: Position[];
	position: number;
	setPosition: (position: number) => void;
}
function StartingPositionDialog({
	bay,
	palletConfig,
	positions,
	position,
	setPosition,
}: StartingPositionDialogProps) {
	return (
		<div
			className="full-width flex-col flex-between align-center full-height"
		>
			<span className="text-align-center font-5 color-blue double-margin-bottom">
				The box highlighted in green shows the next box to be picked
			</span>
			<div className="full-width flex-row flex-around align-end full-height">
				<StackScene
					palletConfig={palletConfig}
					palletIndex={bay.PalletID}
					stackIndex={bay.StackID}
					bayIndex={bay.BayID}
					position={position + 1}
					zoom={1.8}
				/>
			</div>
			<div className="full-width flex-row flex-between">
				<div style={{ paddingLeft: '3em' }}>
					<DirectionalButton
						onClick={() => setPosition(position - 1)}
						direction="left"
						disabled={position <= 0}
						backgroundColour={'rgb(91, 196, 126)'}
					/>
				</div>
				<div style={{ width: '70%' }}>
					<Slider
						step={1}
						minValue={0}
						maxValue={positions.length - 1}
						onChange={setPosition}
						initialValue={position}
						tooltipText={getTooltipText(positions, position)}
					/>
				</div>
				<div style={{ paddingRight: '3em' }}>
					<DirectionalButton
						onClick={() => setPosition(position + 1)}
						direction="right"
						disabled={position >= positions.length - 1}
						backgroundColour={'rgb(91, 196, 126)'}
					/>
				</div>
			</div>
		</div>
	);
}

// -----------------------------------------
// Choose starting side
// -----------------------------------------
interface StartingSideDialogProps {
	bays: Bay[];
	palletConfig: PalletConfigObj;
	stackQueueItem: StackQueueItem;
	startingSide: number;
	setStartingSide: (pStartingSide: number) => void;
}
function StartingSideDialog({
	bays,
	palletConfig,
	stackQueueItem,
	startingSide,
	setStartingSide,
}: StartingSideDialogProps) {
	return (
		<div
			key={stackQueueItem.BayID}
			className={
				(stackQueueItem.BayID === startingSide
					? 'blue-inner-border'
					: 'inner-border semi-transparent') +
				' double-padding-left double-padding-right padding-top padding-bottom flex-col align-center rounded'
			}
			onClick={() => {
				setStartingSide(stackQueueItem.BayID);
			}}
			style={{ height: '38vh', width: '28vh' }}
		>
			<StackScene
				palletConfig={palletConfig}
				palletIndex={stackQueueItem.PalletID}
				stackIndex={stackQueueItem.StackID}
				bayIndex={stackQueueItem.BayID}
				useHighlights={false}
				zoom={1.8}
				enableControls={false}
				autoRotate={startingSide === stackQueueItem.BayID}
			/>
			<h4
				style={{
					color:
						startingSide === stackQueueItem.BayID ? '#1c344f' : 'rgb(135, 135, 135)',
					fontWeight: startingSide === stackQueueItem.BayID ? 'bold' : 'normal',
				}}
			>
				{bays[stackQueueItem.BayID].Name} Pallet
			</h4>
		</div>
	);
}

interface PalletizerStartDialogProps {
	bays: Bay[];
	queue: StackQueue;
	palletConfig: PalletConfigObj;
	updateQueueStackItem: (pStackQueueItem: StackQueueItem) => void;
	exitStart: (pAbort: boolean) => void;
	handleStart: (
		startBay: number,
		palletIndexes: { [key: number]: number }
	) => void;
	waitingForConfirm: boolean;
	leftPositions: Position[];
	rightPositions: Position[];
	initialLeftPosition: number;
	initialRightPosition: number;
	updateQueueItem: (
		pStackQueueItem: StackQueueItem,
		pPosition: number,
		pPositions: Position[]
	) => StackQueueItem;
	pendingSKUScan: {
		[key: string]: string;
	};
}

export function PalletizerStartDialog({
	bays,
	queue,
	palletConfig,
	updateQueueStackItem,
	exitStart,
	handleStart,
	waitingForConfirm,
	leftPositions,
	rightPositions,
	initialLeftPosition,
	initialRightPosition,
	updateQueueItem,
	pendingSKUScan,
}: PalletizerStartDialogProps) {
	const app = useContext(ApplicationContext) as Application;
	const coreAppConfig = useCoreAppConfig(app).coreAppConfig;
	const barcodeStartMode = getBarcodeStartModeEnabled();

	const [activeStep, setActiveStep] = useState<number>(0);
	const [bayQueueItems, setBayQueueItems] = useState<StackQueueItem[]>([]);

	const [palletIndexes, setPalletIndexes] = useState<number[]>([]);

	const [startingStep, setStartingStep] = useState<number>(-1);
	const [startingSide, setStartingSide] = useState<number>(0);

	const [stepTitles, setStepTitles] = useState<string[]>([]);
	const [stackTitles, setStackTitles] = useState<string[]>([]);
	const [leftPosition, setLeftPosition] = useState<number>(
		initialLeftPosition >= 0 ? initialLeftPosition : 0
	);
	const [rightPosition, setRightPosition] = useState<number>(
		initialRightPosition >= 0 ? initialRightPosition : 0
	);
	const [leftStackItem, setLeftStackItem] = useState<StackQueueItem | undefined>(
		undefined
	);
	const [rightStackItem, setRightStackItem] = useState<
		StackQueueItem | undefined
	>(undefined);
	// bay ID : pallet Idx
	const [bayPalletMap, setBayPalletMap] = useState<{ [key: number]: number }>({
		0: 0,
		1: 0,
	});

	useEffect(() => {
		if (bayQueueItems.length === 0) return;
		const left = bayQueueItems.find(item => item.BayID === 0);
		const right = bayQueueItems.find(item => item.BayID === 1);
		setLeftStackItem(left || undefined);
		setRightStackItem(right || undefined);
	}, [bayQueueItems]);

	useEffect(() => {
		if (!leftStackItem) return;
		setLeftStackItem(_ =>
			updateQueueItem(leftStackItem, leftPosition, leftPositions)
		);
	}, [leftPosition]);

	useEffect(() => {
		if (!rightStackItem) return;
		setLeftStackItem(_ =>
			updateQueueItem(rightStackItem, rightPosition, rightPositions)
		);
	}, [rightPosition]);

	useEffect(() => {
		if (!coreAppConfig) return;

		const palletCount = getConfigValueFromKey('multi_pallet', coreAppConfig)
			? 3
			: 1;
		setBayPalletMap({ 0: palletCount - 1, 1: palletCount - 1 });

		// Filter queue to include only stacks with enabled bays
		let filteredQueue = queue.filter(
			(item: StackQueueItem) => bays[item.BayID]?.Enabled
		);

		if (waitingForConfirm) {
			// Only include stacks that are awaiting SKU confirmation
			const infeedIdsNeedingConfirm = Object.keys(pendingSKUScan)
				.filter(key => !!pendingSKUScan[key])
				.map(Number);

			filteredQueue = filteredQueue.filter(item =>
				infeedIdsNeedingConfirm.includes(item.InfeedID)
			);
		}

		const stepTitles: string[] = [];
		const stackTitles: string[] = [];
		const palletStepIndexes: number[] = [];

		filteredQueue.forEach(item => {
			const bayName = bays[item.BayID].Name;
			const stackTitle = getStackAndSkuTitle(item, barcodeStartMode, palletConfig);

			stepTitles.push(`Confirm ${bayName} Stack`);
			stackTitles.push(stackTitle);

			if (palletCount > 1) {
				stepTitles.push(`Confirm ${bayName} Pallets`);
				palletStepIndexes.push(stepTitles.length - 1);
				stackTitles.push(`Pallets For ${stackTitle}`);
			}
		});

		setPalletIndexes(palletStepIndexes);
		setBayQueueItems(filteredQueue);
		setStackTitles(stackTitles);

		let allSteps = [...stepTitles];

		if (
			filteredQueue.length > 1 &&
			!waitingForConfirm &&
			!getConfigValueFromKey('async', coreAppConfig)
		) {
			allSteps.push('Choose Start Side');
			setStartingStep(allSteps.length - 1);
		} else if (filteredQueue.length === 1) {
			setStartingSide(filteredQueue[0].BayID);
		}

		setStepTitles(allSteps);
	}, [bays, queue, coreAppConfig]);
	return (
		<StartDialog
			title={'Palletizer Setup'}
			stepTitles={stepTitles}
			activeStep={activeStep}
			activeStepReady={true}
			setActiveStep={setActiveStep}
			handleExit={exitStart}
			handleStart={() => {
				if (leftStackItem) updateQueueStackItem(leftStackItem);
				if (rightStackItem) updateQueueStackItem(rightStackItem);
				handleStart(startingSide, bayPalletMap);
			}}
			waitingForConfirm={waitingForConfirm}
		>
			<div className="full-width flex-col align-center" style={{ height: '88%' }}>
				<span className="font-med-big bold-font color-blue">
					{stackTitles[activeStep]}
				</span>
				<div className="half-padding"></div>
				{/* Choose starting/recovery position */}
				{!!palletConfig &&
					bayQueueItems.map(
						(bay, bayIndex) =>
							bayIndex === activeStep && (
								<StartingPositionDialog
									key={bay.BayID}
									bay={bay}
									palletConfig={palletConfig}
									positions={bay.BayID === 0 ? leftPositions : rightPositions}
									position={bay.BayID === 0 ? leftPosition : rightPosition}
									setPosition={bay.BayID === 0 ? setLeftPosition : setRightPosition}
								/>
							)
					)}
				{/* Multi-Pallet stuff */}
				{palletIndexes.map((pPalletStepIdx, pIdx) => {
					if (pPalletStepIdx === activeStep) {
						const lStackForBay = bayQueueItems[pIdx];
						if (!lStackForBay) {
							return void 0;
						}
						<MultiPalletDialog
							bayQueueItems={bayQueueItems}
							bay={pIdx}
							position={pIdx === 0 ? leftPosition : rightPosition}
							bayPalletMap={bayPalletMap}
							setBayPalletMap={setBayPalletMap}
							palletConfig={palletConfig}
						/>;
					} else {
						return void 0;
					}
				})}
				{/* Choose starting side */}
				{activeStep === startingStep && (
					<div className="full-width full-height flex-col flex-around align-center">
						<div className="full-width flex-row align-center flex-around">
							{!!palletConfig &&
								bayQueueItems.map(stackQueueItem => (
									<StartingSideDialog
										bays={bays}
										palletConfig={palletConfig}
										startingSide={startingSide}
										setStartingSide={setStartingSide}
										stackQueueItem={stackQueueItem}
									/>
								))}
						</div>
					</div>
				)}
			</div>
		</StartDialog>
	);
}
